import { getAuthorizationHeader } from 'donor/utils';
import {
  ApiResponse,
  setErrorResponse,
} from 'shared/auth/hooks/useRequestState';
import { LEGACY_API_URL } from 'shared/utils/constants';
import type { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';

interface JsonApiRelationshipLinks {
  self: string;
  related: string;
}

interface JsonApiCollectionResponse<T> {
  data: T[];
  links?: {
    self: string;
  };
}

export interface ReportNoteSummary {
  createdAt: string;
  message: string;
  type: string;
  updatedAt: string | null;
  id: number;
}

export interface BudgetFileSummary {
  createdAt: string;
  updatedAt: string | null;
  mime: string;
  name: string;
  url: string;
  id: number;
}

interface LegacyApiSuccessResponse<T> {
  success: true;
  data: T;
}

export interface BudgetFileAttributes {
  upload_status: unknown;
  created_at: string;
  updated_at: string | null;
  _h_ghosted_by: number | null;
  name: string;
  _version: number;
  tags: unknown[];
  mime: string;
  url: string;
  use_type: string;
  _h_account_id: number;
  _h_operation: string;
  _h_user_id: number;
}

export interface BudgetFileResource {
  type: 'files' | string;
  id: number;
  attributes: BudgetFileAttributes;
  relationships?: {
    account?: {
      links: JsonApiRelationshipLinks;
    };
  };
  links?: {
    self: string;
  };
}

export interface ReportNoteAttributes {
  type: string;
  message: string;
  created_at: string;
  updated_at: string | null;
  tags: unknown[];
  _h_ghosted_by: number | null;
  _h_operation: string;
  _h_account_id: number;
  _h_user_id: number;
  _version: number;
}

export interface ReportNoteResource {
  type: 'report_notes' | string;
  id: number;
  attributes: ReportNoteAttributes;
  relationships?: {
    report?: {
      links: JsonApiRelationshipLinks;
    };
  };
  links?: {
    self: string;
  };
}

interface LegacyApiErrorBody {
  message?: string;
  Message?: string;
}

const getErrorMessage = async (res: Response) => {
  let message = 'Something went wrong while sending the request';
  try {
    const body = (await res.json()) as LegacyApiErrorBody;
    message = body.message || body.Message || message;
  } catch (e) {
    // ignore json parse errors
  }
  return message;
};

export const getBudgetFiles = async (
  reportId: string | number,
): Promise<ApiResponse<BudgetFileSummary[]>> => {
  try {
    const token = getAuthorizationHeader();
    const res = await fetch(
      `${LEGACY_API_URL}/reports/${reportId}/budget_files`,
      {
        headers: {
          'ti-id-token': token || '',
          accept: 'application/json',
        },
      },
    );

    if (!res.ok) {
      const message = await getErrorMessage(res);
      return {
        success: false,
        error: setErrorResponse({
          status: res.status,
          message,
          isAxiosError: false,
          name: res.statusText || 'Budget Files Fetch Error',
        }),
      };
    }

    const body = (await res.json()) as JsonApiCollectionResponse<
      BudgetFileResource
    >;
    const summaries: BudgetFileSummary[] = body.data.map(file => ({
      createdAt: file.attributes.created_at,
      updatedAt: file.attributes.updated_at,
      mime: file.attributes.mime,
      name: file.attributes.name,
      url: file.attributes.url,
      id: file.id,
    }));
    return { success: true, data: summaries };
  } catch (e) {
    return {
      success: false,
      error: setErrorResponse({
        status: 500,
        message: 'Something went wrong while sending the request',
        isAxiosError: false,
        name: 'Budget Files Fetch Error',
      }),
    };
  }
};

export const getReportNotes = async (
  reportId: string | number,
): Promise<ApiResponse<ReportNoteSummary[]>> => {
  try {
    const token = getAuthorizationHeader();
    const res = await fetch(`${LEGACY_API_URL}/reports/${reportId}/notes`, {
      headers: {
        'ti-id-token': token || '',
        accept: 'application/json',
      },
    });

    if (!res.ok) {
      const message = await getErrorMessage(res);
      return {
        success: false,
        error: setErrorResponse({
          status: res.status,
          message,
          isAxiosError: false,
          name: res.statusText || 'Report Notes Fetch Error',
        }),
      };
    }

    const body = (await res.json()) as unknown;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resources = ((body as any)?.data?.data ??
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (body as any)?.data ??
      []) as ReportNoteResource[];

    const summaries: ReportNoteSummary[] = resources.map(note => ({
      createdAt: note.attributes.created_at,
      message: note.attributes.message,
      type: note.attributes.type,
      updatedAt: note.attributes.updated_at,
      id: note.id,
    }));

    return { success: true, data: summaries };
  } catch (e) {
    return {
      success: false,
      error: setErrorResponse({
        status: 500,
        message: 'Something went wrong while sending the request',
        isAxiosError: false,
        name: 'Report Notes Fetch Error',
      }),
    };
  }
};

export const mapTrueImpactFile = (
  files: BudgetFileSummary[],
): TrueImpactFile[] => {
  return files.map(f => ({
    id: f.id,
    fileName: f.name,
    url: f.url,
    description: undefined,
  }));
};
